import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Flex,
} from '@bridebook/aura';
import { SearchType } from '@bridebook/toolbox/src/search-suppliers/types';
import { IUISupplier } from '@bridebook/toolbox/src/types';
import { SearchListItemLoggedOut } from '@bridebook/web/app-basic/components/search/search-list-item/search-list-item';
import { ILocationData } from '@bridebook/web/app-shared/lib/search/types';
import partition from 'lodash/partition';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { PremiumTiers } from '@bridebook/toolbox/src/constants';
import { useRouter } from 'next/router';

interface IProps {
  suppliers: IUISupplier[];
  locationData: ILocationData | null;
  locationName: string;
}

const MORE_VENUES_ACCORDION = 'more-venues-accordion';

const HiddenLimitedProfilesList = ({ suppliers, locationData, locationName }: IProps) => {
  const { t } = useTranslation('search');
  const router = useRouter();

  const location = useMemo(
    () =>
      locationData
        ? locationData.type === SearchType.County
          ? locationData.county || locationData.country
          : locationData.town || locationData.name || locationName
        : locationName,
    [locationData, locationName],
  );

  const [payingVenues, nonPayingVenues] = useMemo(
    () => partition(suppliers, ({ tier }) => tier !== undefined && tier > PremiumTiers.Tier_0),
    [suppliers],
  );

  const accordionValue =
    router.query.showMore === 'true' || payingVenues.length === 0 ? MORE_VENUES_ACCORDION : '';

  // Paying venues render first; if there are none, the accordion is open by default
  // and the first non-paying venue becomes the first visible (LCP) card.
  const firstVisibleSupplierId = (payingVenues[0] ?? nonPayingVenues[0])?.id;

  const handleAccordionChange = useCallback(
    (value: string) => {
      const { showMore, ...rest } = router.query;
      void router.push({ query: { ...rest, ...(value && { showMore: 'true' }) } }, undefined, {
        shallow: true,
      });
    },
    [router],
  );

  return (
    <>
      {payingVenues.map((supplier, index) => (
        <SearchListItemLoggedOut
          key={supplier.id}
          supplier={supplier}
          itemIndex={supplier.id === firstVisibleSupplierId ? 0 : index + 1}
        />
      ))}
      {nonPayingVenues.length > 0 && (
        <Accordion
          variant="bordered"
          type="single"
          collapsible
          className="mb-4 w-full"
          value={accordionValue}
          onValueChange={handleAccordionChange}>
          <AccordionItem value={MORE_VENUES_ACCORDION}>
            <AccordionTrigger>{t('limitedVenuesList.moreVenuesIn', { location })}</AccordionTrigger>
            <AccordionContent forceMount classNames={{ wrapper: 'data-[state=closed]:h-0' }}>
              <Flex className="flex flex-col gap-6">
                {nonPayingVenues.map((supplier, index) => (
                  <SearchListItemLoggedOut
                    key={supplier.id}
                    supplier={supplier}
                    itemIndex={
                      supplier.id === firstVisibleSupplierId ? 0 : payingVenues.length + index + 1
                    }
                  />
                ))}
              </Flex>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </>
  );
};

export default memo(HiddenLimitedProfilesList);
